import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight, Bot, ChevronsUpDown, CircleUserRound, FileText, House, LayoutGrid, LifeBuoy, LogOut, Megaphone, Menu, MessagesSquare, PanelLeftClose, PanelLeftOpen, PlugZap, ReceiptText, SlidersHorizontal, Tags, UsersRound, X } from 'lucide-react';
import sidebarMark from 'src/assets/branding/auto-appy-mark-256x256.png';
import { AuthContext } from 'src/context/AuthContext';
import { autoAppyAppInitials, autoAppyAppLaunchUrl, autoAppyProductMark, autoAppySwitcherApps } from 'src/config/autoAppyApps';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import { FOOTER_ITEMS, activeItemId, visibleGroups } from './stageNav';
import useConnectedNumber from './useConnectedNumber';
import { Monogram, WsDialog } from './StageUi';
import './stage.css';
import './wa-stage.css';

// AUTO-WA-39: the main app's AUTO-319 WorkspaceSidebar, for WhatsApp. Same
// widths, rail, groups, footer, product switcher and account menu.
const ICONS = {
  home: House, chats: MessagesSquare, broadcasts: Megaphone, contacts: UsersRound, templates: FileText, tags: Tags,
  bots: Bot, billing: ReceiptText, setup: PlugZap, settings: SlidersHorizontal, help: LifeBuoy,
};
// Same tones as the main app's switcher (AI uses the brand sky).
export const PRODUCT_TONES = { main: '#147DB5', whatsapp: '#1F9D57', crm: '#183F5A', 'auto-appy-ai': '#37A1D2', ai: '#37A1D2' };
const RAIL_KEY = 'autoappy.sidebarRail';
const CURRENT_APP = 'whatsapp';

const readRail = () => {
  try { return window.localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
};

function usePopover() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);

  const toggle = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      const width = 300;
      const left = Math.min(rect.right + 10, window.innerWidth - width - 12);
      const bottom = Math.max(12, window.innerHeight - rect.bottom);
      setPosition({ left: Math.max(12, left), bottom });
    }
    setOpen((value) => !value);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!panelRef.current?.contains(event.target) && !anchorRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); anchorRef.current?.focus(); } };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return { open, setOpen, toggle, position, anchorRef, panelRef };
}

function NavLink({ item, active, rail, onNavigate }) {
  const Icon = ICONS[item.icon] || House;
  return (
    <Link
      to={item.to}
      className={`ws-sb-item${active ? ' is-active' : ''}`}
      aria-current={active ? 'page' : undefined}
      title={rail ? item.label : undefined}
      aria-label={rail ? item.label : undefined}
      onClick={onNavigate}
    >
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
      <span className="ws-sb-text">{item.label}</span>
    </Link>
  );
}

export default function StageSidebar({ connection }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const [rail, setRail] = useState(readRail);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const products = usePopover();
  const account = usePopover();
  const number = useConnectedNumber(connection?.phoneNumberId);

  const connected = Boolean(connection);
  const organisation = currentUser?.current_workspace?.name || currentUser?.workspaces?.[0]?.name || 'Your organisation';
  const numberName = number?.display_name || number?.verified_name;
  // The number's own name, unless it just repeats the organisation name.
  const numberLabel = number
    ? (numberName && numberName !== organisation ? numberName : number.phone_number || numberName)
    : (connected ? 'WhatsApp number' : 'Not connected yet');
  const activeId = activeItemId(location.pathname);
  const apps = autoAppySwitcherApps(currentUser?.apps);
  const name = currentUser?.name || currentUser?.username || 'Your account';

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    try { window.localStorage.setItem(RAIL_KEY, rail ? '1' : '0'); } catch { /* private mode */ }
  }, [rail]);

  const closeMobile = () => setMobileOpen(false);
  const showRail = rail && !mobileOpen;

  const signOut = async () => {
    setLeaving(true);
    try { await logout(); } finally {
      setLeaving(false);
      setLogoutOpen(false);
      navigate('/', { replace: true });
    }
  };

  return (
    <>
      <button type="button" className="ws-hamburger" onClick={() => setMobileOpen((open) => !open)} aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen}>
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {mobileOpen && <div className="ws-scrim" onClick={closeMobile} aria-hidden="true" />}
      <div className={`ws-sidebar-shell${mobileOpen ? ' is-open' : ''}`}>
        <nav className={`ws-sidebar${showRail ? ' is-rail' : ''}`} aria-label="Main navigation">
          <div className="ws-sb-top">
            <img src={sidebarMark} alt="" />
            <span className="wa-sb-brandstack">
              <span className="ws-sb-brand">Auto <span>Appy</span></span>
              <span className="wa-sb-product">WhatsApp</span>
            </span>
            <button type="button" className="ws-sb-iconbtn" onClick={() => setRail((value) => !value)} aria-label={showRail ? 'Expand sidebar' : 'Collapse sidebar'} title={showRail ? 'Expand sidebar' : 'Collapse sidebar'}>
              {showRail ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            </button>
          </div>

          <Link to="/settings/connection" className="ws-sb-org" onClick={closeMobile} title={showRail ? `${organisation} · ${numberLabel}` : undefined} aria-label={`${organisation}, ${numberLabel}. Connection settings`}>
            <span className="ws-sb-org-mark wa-sb-org-mark" aria-hidden="true">{autoAppyAppInitials(organisation).slice(0, 1)}</span>
            <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
              <span className="ws-sb-org-name" style={{ display: 'block' }}>{organisation}</span>
              <span className="ws-sb-org-sub wa-sb-number">
                <i className={connected ? 'is-on' : ''} aria-hidden="true" />{numberLabel}
              </span>
            </span>
            <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
          </Link>

          <div className="ws-sb-scroll">
            {visibleGroups(connected).map((group) => (
              <div className="ws-sb-group" key={group.id} role="group" aria-label={group.label || 'WhatsApp'}>
                {group.label && <div className="ws-sb-label">{group.label}</div>}
                {group.items.map((item) => (
                  <NavLink key={item.id} item={item} active={activeId === item.id} rail={showRail} onNavigate={closeMobile} />
                ))}
              </div>
            ))}
          </div>

          <div className="ws-sb-footer">
            {FOOTER_ITEMS.map((item) => (
              <NavLink key={item.id} item={item} active={activeId === item.id} rail={showRail} onNavigate={closeMobile} />
            ))}
            <button type="button" ref={products.anchorRef} className="ws-sb-item" onClick={products.toggle} aria-haspopup="dialog" aria-expanded={products.open} title={showRail ? 'Products' : undefined} aria-label={showRail ? 'Products' : undefined}>
              <span className="ws-sb-dots" aria-hidden="true"><i style={{ background: '#147DB5' }} /><i style={{ background: '#7CC4E8' }} /><i style={{ background: '#1F9D57' }} /><i style={{ background: '#183F5A' }} /></span>
              <span className="ws-sb-text">Products</span>
              <ArrowUpRight className="ws-sb-meta" size={14} aria-hidden="true" />
            </button>
            <button type="button" ref={account.anchorRef} className="ws-sb-me" onClick={account.toggle} aria-haspopup="menu" aria-expanded={account.open} aria-label={`Account: ${name}`}>
              <Monogram name={name} size={32} />
              <span className="ws-sb-org-text ws-grow" style={{ minWidth: 0 }}>
                <span className="ws-sb-org-name" style={{ display: 'block' }}>{name}</span>
                <span className="ws-sb-org-sub" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email || 'Profile and sign out'}</span>
              </span>
              <ChevronsUpDown className="ws-sb-chevron" size={14} color="#5B7180" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </div>

      {products.open && products.position && (
        <div ref={products.panelRef} className="ws-popover ws-overlay" role="dialog" aria-label="Switch product" style={{ left: products.position.left, bottom: products.position.bottom }}>
          <div className="ws-popover-title">Switch product</div>
          {apps.length === 0 && <p className="ws-muted" style={{ padding: '4px 10px 10px' }}>No other products are enabled for this account.</p>}
          {apps.map((app) => (
            <a key={app.key} className="ws-menu-item" href={autoAppyAppLaunchUrl(app, currentUser?.current_workspace?.id)} onClick={() => products.setOpen(false)} aria-current={app.key === CURRENT_APP ? 'page' : undefined}>
              <span className="ws-product-icon" style={{ background: PRODUCT_TONES[app.key] || '#3A5264' }}>{autoAppyProductMark(app)}</span>
              <span className="ws-grow">{app.key === 'main' ? 'Auto Appy' : app.name}</span>
              {app.key === CURRENT_APP ? <span className="ws-chip">Current</span> : <ArrowUpRight size={15} aria-hidden="true" />}
            </a>
          ))}
          <a className="ws-menu-item" href={autoAppyUrl('/autoappy/apps')} onClick={() => products.setOpen(false)} style={{ color: '#0F6392', fontWeight: 500 }}>
            <LayoutGrid size={16} aria-hidden="true" />View the complete app library
          </a>
        </div>
      )}

      {account.open && account.position && (
        <div ref={account.panelRef} className="ws-popover ws-overlay" role="menu" aria-label="Account" style={{ left: account.position.left, bottom: account.position.bottom, width: 260 }}>
          <a role="menuitem" className="ws-menu-item" href={autoAppyUrl('/profile')} onClick={() => account.setOpen(false)}><CircleUserRound size={16} />Profile in Auto Appy</a>
          <button role="menuitem" type="button" className="ws-menu-item" onClick={() => { account.setOpen(false); setLogoutOpen(true); }} style={{ color: '#AD3B3B' }}><LogOut size={16} color="#AD3B3B" />Log out</button>
        </div>
      )}
      {logoutOpen && (
        <WsDialog title="Log out of Auto Appy?" description="This signs you out of every Auto Appy product. You will need to sign in again to continue." onClose={() => !leaving && setLogoutOpen(false)}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => setLogoutOpen(false)} disabled={leaving}>Cancel</button>
            <button type="button" className="ws-btn ws-btn--danger" onClick={signOut} disabled={leaving}><LogOut size={15} />{leaving ? 'Logging out…' : 'Log out'}</button>
          </div>
        </WsDialog>
      )}
    </>
  );
}
